/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: TemplateForm.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { LayoutTemplate, Loader2, Search, Copy, Plus } from 'lucide-react';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { useContextStore } from '../../store/context';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import ModuleFormHeader from '../../components/layouts/ModuleFormHeader';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { fetchPhonebooks } from '../phonebooks/api/phonebooksApi';
import * as api from './api';

import { DssKeyConfig, ExpansionKeyConfig, PhonebookEntryConfig } from './types';
import {
    extractDssKeysInfo,
    extractExpansionModuleInfo,
} from './utils/extractOptions';

import { GeneralTab } from './tabs/GeneralTab';
import { DssKeysTab } from './tabs/DssKeysTab';
import { PhonebookTab } from './tabs/PhonebookTab';
import { ExpansionModulesTab } from './tabs/ExpansionModulesTab';
import { GuiTab } from './tabs/GuiTab';
import { CustomTab } from './tabs/CustomTab';

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

export default function TemplateForm() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const duplicateId = searchParams.get('duplicate');
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();
    const isNew = !id || id === 'new';

    const [showQuickList, setShowQuickList] = useState(false);
    const [quickListItems, setQuickListItems] = useState<api.Template[]>([]);
    const [initialQuickListItems, setInitialQuickListItems] = useState<api.Template[]>([]);
    const [quickListSearch, setQuickListSearch] = useState('');
    const [isSearchingQuickList, setIsSearchingQuickList] = useState(false);
    const [isLoadingQuickListInitial, setIsLoadingQuickListInitial] = useState(false);
    const quickListLoadingRef = React.useRef(false);

    const [vendors, setVendors] = useState<api.Vendor[]>([]);
    const [serverSettings, setServerSettings] = useState<api.ServerSetting[]>([]);
    const [models, setModels] = useState<api.DeviceModel[]>([]);
    const [selectedModel, setSelectedModel] = useState<api.DeviceModel | null>(null);
    const [initialData, setInitialData] = useState<api.Template | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('general');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [dssKeys, setDssKeys] = useState<DssKeyConfig[]>([]);
    const [dssPage, setDssPage] = useState(1);
    const [dssPerPage, setDssPerPage] = useState(10);
    const [expansionKeys, setExpansionKeys] = useState<ExpansionKeyConfig[]>([]);
    const [selectedExpModule, setSelectedExpModule] = useState(1);
    const [expPage, setExpPage] = useState(1);
    const [expPerPage, setExpPerPage] = useState(10);
    const [phonebookEntries, setPhonebookEntries] = useState<PhonebookEntryConfig[]>([]);
    const [availablePhonebooks, setAvailablePhonebooks] = useState<{ id?: number; uuid?: string; description: string; url?: string }[]>([]);
    const [originalTemplateContent, setOriginalTemplateContent] = useState<string>('');
    const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
    const [selectedWallpaperFile, setSelectedWallpaperFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewImageSrc, setPreviewImageSrc] = useState<string | null>(null);

    const { register, control, handleSubmit, watch, setValue, reset, getValues, formState: { errors, isSubmitting } } = useForm<api.Template>({
        defaultValues: { name: '', keys: [], expansionModules: [], phonebook: [], customConfig: '' }
    });

    const watchedVendorId = watch('vendorId');
    const watchedModelId = watch('modelId');

    // Wallpaper preview URL
    useEffect(() => {
        if (!selectedWallpaperFile) { setPreviewUrl(null); return; }
        const objectUrl = URL.createObjectURL(selectedWallpaperFile);
        setPreviewUrl(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [selectedWallpaperFile]);

    // Model image
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((selectedModel as any)?.imageFile) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const rawPath = (selectedModel as any).imageFile.replace(/\\/g, '/');
            const safePath = rawPath.split('/').map((s: string) => encodeURIComponent(s)).join('/');
            setPreviewImageSrc(`/uploads/${safePath}`);
        } else {
            setPreviewImageSrc(null);
        }
    }, [selectedModel]);

    const handleImageError = () => {
        if (!previewImageSrc || !selectedModel) return;
        if (previewImageSrc.includes('%20')) {
            const newSrc = previewImageSrc.replace(/%20/g, '-');
            if (newSrc !== previewImageSrc) { setPreviewImageSrc(newSrc); return; }
        }
        setPreviewImageSrc(null);
    };

    // Load initial data
    useEffect(() => {
        const init = async () => {
            setIsLoading(true);
            try {
                const [vendorsData, serverSettingsData, phonebooksData] = await Promise.all([
                    api.getVendors(),
                    api.getServerSettings(),
                    fetchPhonebooks({ page: 1, limit: 1000, sortBy: 'description', sortOrder: 'asc' })
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        .then(res => Array.isArray(res.data) ? res.data : ((res.data as any).data || []))
                        .catch(() => [])
                ]);
                setVendors(vendorsData);
                setServerSettings(serverSettingsData);
                setAvailablePhonebooks(phonebooksData);
                const targetId = !isNew ? id : duplicateId;
                if (targetId) {
                    const template = await api.getTemplate(targetId);
                    if (template.vendorId) {
                        const allModels = await api.getDeviceModels();
                        const vendorModels = allModels.filter(m => m.vendorId === template.vendorId);
                        setModels(vendorModels);
                        const modelDetails = vendorModels.find(m => m.id === template.modelId);
                        if (modelDetails) {
                            const fullModel = await api.getDeviceModel(template.modelId);
                            setSelectedModel(fullModel);
                        }
                    }
                    if (template.phonebook && Array.isArray(template.phonebook)) {
                        setPhonebookEntries(template.phonebook.map((entry: Record<string, unknown>) => ({ ...entry, id: generateId() })));
                    }
                    if (template.customConfig) setValue('customConfig', template.customConfig);
                    if (duplicateId) {
                        template.name = `${template.name} (Copy)`;
                        // @ts-ignore
                        delete template.id;
                    }
                    reset(template);
                    setInitialData(template);
                    if (template.wallpaper) setPreviewUrl(template.wallpaper);
                }
            } catch (error) {
                console.error('Failed to load data', error);
                showError(t('common.loadError', 'Failed to load data'));
            } finally {
                setIsLoading(false);
            }
        };
        init();
    }, [id, isNew, reset]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isNew && selectedDomain) setValue('domainId', selectedDomain.id);
    }, [isNew, selectedDomain, setValue]);

    // Vendor change: load models
    useEffect(() => {
        if (!watchedVendorId) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fetchModels = async () => {
            try {
                const allModels = await api.getDeviceModels();
                setModels(allModels.filter(m => m.vendorId === Number(watchedVendorId)));
            } catch (error) { console.error(error); }
        };
        fetchModels();
    }, [watchedVendorId]);

    // Model change: load details, DSS/Expansion keys, template file
    useEffect(() => {
        if (!watchedModelId) {
            setSelectedModel(prev => prev === null ? prev : null);
            setDssKeys(prev => prev.length === 0 ? prev : []);
            setExpansionKeys(prev => prev.length === 0 ? prev : []);
            return;
        }
        const fetchModelDetails = async () => {
            try {
                const model = await api.getDeviceModel(watchedModelId);
                setSelectedModel(model);
                const dssInfo = extractDssKeysInfo(model.keys, model.accounts);
                if (dssInfo.count > 0) {
                    const existingKeys = getValues('keys');
                    if (Array.isArray(existingKeys) && existingKeys.length > 0) {
                        setDssKeys(existingKeys.map((key: Record<string, unknown>, index: number) => ({
                            keyNumber: index + 1, type: String(key['type'] ?? 'Line'),
                            description: String(key['description'] ?? ''), value: String(key['value'] ?? ''),
                            extension: String(key['extension'] ?? ''), line: String(key['line'] ?? 'Line 1')
                        })));
                    } else {
                        setDssKeys(Array.from({ length: dssInfo.count }, (_, i) => ({
                            keyNumber: i + 1, type: 'Line', description: '', value: '', extension: '', line: 'Line 1'
                        })));
                    }
                    if (model.phonebook) {
                        const existingPhonebook = getValues('phonebook');
                        setPhonebookEntries(Array.isArray(existingPhonebook) && existingPhonebook.length > 0
                            ? existingPhonebook.map((entry: Record<string, unknown>) => ({ ...entry, id: String(entry['id'] ?? generateId()) }))
                            : []);
                    }
                    const expInfo = extractExpansionModuleInfo(model.expansionModuleKeys, model.accounts);
                    if (expInfo.moduleCount > 0 && expInfo.keysPerModule > 0) {
                        const existingExpKeys = getValues('expansionModules');
                        if (Array.isArray(existingExpKeys) && existingExpKeys.length > 0) {
                            setExpansionKeys(existingExpKeys.map((key: Record<string, unknown>, index: number) => {
                                const moduleNum = Math.floor(index / expInfo.keysPerModule) + 1;
                                const keyNum = (index % expInfo.keysPerModule) + 1;
                                return { moduleNumber: moduleNum, keyNumber: keyNum, type: String(key['type'] ?? 'Line'),
                                    description: String(key['description'] ?? ''), value: String(key['value'] ?? ''),
                                    extension: String(key['extension'] ?? ''), line: String(key['line'] ?? 'Line 1') };
                            }));
                        } else {
                            const totalKeys = expInfo.moduleCount * expInfo.keysPerModule;
                            setExpansionKeys(Array.from({ length: totalKeys }, (_, i) => {
                                const moduleNum = Math.floor(i / expInfo.keysPerModule) + 1;
                                return { moduleNumber: moduleNum, keyNumber: (i % expInfo.keysPerModule) + 1,
                                    type: 'Line', description: '', value: '', extension: '', line: 'Line 1' };
                            }));
                        }
                    } else { setExpansionKeys([]); }

                    if (model.templateFile) {
                        setIsLoadingTemplate(true);
                        try {
                            const extensions = ['.cfg', '.xml', '.j2', '.txt'];
                            let templateLoaded = false;
                            const currentTemplateId = id && id !== 'new' ? id : null;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const currentDomainUuid = (initialData as any)?.domain?.uuid || (selectedDomain as any)?.uuid;
                            if (currentTemplateId && currentDomainUuid) {
                                for (const ext of extensions) {
                                    try {
                                        const res = await fetch(`/provisioning/${currentDomainUuid}/templates/${currentTemplateId}/template${ext}`);
                                        if (res.ok) {
                                            const text = await res.text();
                                            setOriginalTemplateContent(text);
                                            if (!getValues('customConfig')?.trim()) setValue('customConfig', text);
                                            templateLoaded = true; break;
                                        }
                                    } catch { /* continue */ }
                                }
                            }
                            if (!templateLoaded) {
                                const basePath = model.templateFile.replace(/\\/g, '/');
                                for (const ext of extensions) {
                                    try {
                                        const res = await fetch(`/uploads/${basePath}${ext}`);
                                        if (res.ok) {
                                            const text = await res.text();
                                            setOriginalTemplateContent(text);
                                            if (!getValues('customConfig')?.trim()) setValue('customConfig', text);
                                            templateLoaded = true; break;
                                        }
                                    } catch { /* continue */ }
                                }
                            }
                        } catch (templateError) {
                            console.warn('Could not load template file:', templateError);
                        } finally { setIsLoadingTemplate(false); }
                    }
                }
            } catch (error) {
                console.error(error);
                showError(t('provisioningTemplates.modelLoadError', 'Failed to load model details'));
            }
        };
        fetchModelDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedModelId]);

    const handleSaveInternal = async () => {
        let result = undefined;
        await handleSubmit(async (data: api.Template) => {
            try {
                if (selectedWallpaperFile) {
                    const toBase64 = (file: File) => new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(file);
                        reader.onload = () => resolve(reader.result as string);
                        reader.onerror = error => reject(error);
                    });
                    data.wallpaper = await toBase64(selectedWallpaperFile);
                }
                if (isNew) {
                    const res = await api.createTemplate(data);
                    showSuccess(t('common.createdSuccessfully', 'Item created successfully'));
                    result = res;
                } else {
                    const res = await api.updateTemplate(id!, data);
                    showSuccess(t('common.updatedSuccessfully', 'Item updated successfully'));
                    result = res;
                }
            } catch (error) {
                console.error(error);
                showError(t('common.error', 'Failed to save item'));
                throw error;
            }
        })();
        return result;
    };

    const handleSaveWithAction = useSaveWithAction({
        onSave: handleSaveInternal,
        listPath: '/settings/provisioning/provisioning-templates',
        newPath: '/settings/provisioning/provisioning-templates/new',
        mode: isNew ? 'create' : 'edit',
        getEditPath: (savedData) => `/settings/provisioning/provisioning-templates/${savedData.id}`,
        getFormData: () => ({ ...getValues() }),
        storageKey: 'provisioningTemplates:saveAction'
    });

    const handleCancel = () => {
        const dataToRestore = initialData || { name: '', keys: [], expansionModules: [], phonebook: [], customConfig: '' };
        reset(dataToRestore);
        setPhonebookEntries(dataToRestore.phonebook && Array.isArray(dataToRestore.phonebook)
            ? dataToRestore.phonebook.map((entry: Record<string, unknown>) => ({ ...entry, id: generateId() }))
            : []);
        if (selectedModel) {
            const dssInfo = extractDssKeysInfo(selectedModel.keys, selectedModel.accounts);
            const keys = dataToRestore.keys || [];
            if (Array.isArray(keys) && keys.length > 0) {
                setDssKeys(keys.map((key: Record<string, unknown>, index: number) => ({
                    keyNumber: index + 1, type: String(key['type'] ?? 'Line'), description: String(key['description'] ?? ''),
                    value: String(key['value'] ?? ''), extension: String(key['extension'] ?? ''), line: String(key['line'] ?? 'Line 1')
                })));
            } else if (dssInfo.count > 0) {
                setDssKeys(Array.from({ length: dssInfo.count }, (_, i) => ({
                    keyNumber: i + 1, type: 'Line', description: '', value: '', extension: '', line: 'Line 1'
                })));
            } else { setDssKeys([]); }
        }
    };

    const loadInitialQuickList = React.useCallback(async () => {
        if (quickListLoadingRef.current) return;
        quickListLoadingRef.current = true;
        setIsLoadingQuickListInitial(true);
        try {
            const result = await api.getTemplates();
            setInitialQuickListItems(result);
            setQuickListItems(result);
        } catch { setInitialQuickListItems([]); setQuickListItems([]); }
        finally { setIsLoadingQuickListInitial(false); quickListLoadingRef.current = false; }
    }, []);

    useEffect(() => {
        if (showQuickList && initialQuickListItems.length === 0 && !quickListLoadingRef.current) {
            void loadInitialQuickList();
        }
    }, [showQuickList, initialQuickListItems.length, loadInitialQuickList]);

    useEffect(() => {
        if (!showQuickList) { setQuickListSearch(''); return; }
        const timer = setTimeout(() => {
            const q = quickListSearch.trim().toLowerCase();
            setIsSearchingQuickList(true);
            setQuickListItems(q.length < 2 ? initialQuickListItems : initialQuickListItems.filter(t => t.name.toLowerCase().includes(q)));
            setIsSearchingQuickList(false);
        }, 300);
        return () => clearTimeout(timer);
    }, [quickListSearch, showQuickList, initialQuickListItems]);

    if (isLoading) {
        return <div className="p-8 flex justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setDssKeysTyped = (updater: (prev: DssKeyConfig[]) => DssKeyConfig[]) => setDssKeys(updater);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setExpansionKeysTyped = (updater: (prev: ExpansionKeyConfig[]) => ExpansionKeyConfig[]) => setExpansionKeys(updater);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setPhonebookEntriesTyped = (updater: (prev: PhonebookEntryConfig[]) => PhonebookEntryConfig[]) => setPhonebookEntries(updater);

    return (
        <div className="p-6 pb-20">
            <ModuleFormHeader
                icon={<LayoutTemplate className="h-5 w-5 text-primary" />}
                title={isNew ? t('provisioningTemplates.create', 'Create Template') : t('provisioningTemplates.edit', 'Edit Template')}
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate('/settings/provisioning/provisioning-templates')}
                actions={[
                    { icon: <Search className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.search', 'Quick Search'), onClick: () => setShowQuickList(true) },
                    ...(!isNew ? [{ icon: <Copy className="h-5 w-5 text-muted-foreground" />, tooltip: t('common.duplicate', 'Duplicate'), onClick: () => navigate(`/settings/provisioning/provisioning-templates/new?duplicate=${id}`) }] : []),
                    { icon: <Plus className="h-5 w-5 text-muted-foreground" />, tooltip: t('provisioningTemplates.add'), onClick: () => navigate('/settings/provisioning/provisioning-templates/new') }
                ]}
            />

            <div className="mt-8">
                <form>
                    <input type="hidden" {...register('domainId')} />
                    <Tabs value={activeTab} onValueChange={setActiveTab}>
                        <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1">
                            <TabsTrigger value="general">{t('common.generalInfo', 'General Information')}</TabsTrigger>
                            {selectedModel && (
                                <>
                                    {extractDssKeysInfo(selectedModel.keys, selectedModel.accounts).count > 0 && (
                                        <TabsTrigger value="dss">{t('provisioningTemplates.tabs.dss', 'DSS KEYS')}</TabsTrigger>
                                    )}
                                    {(() => { const e = extractExpansionModuleInfo(selectedModel.expansionModuleKeys, selectedModel.accounts); return e.moduleCount > 0 && e.keysPerModule > 0; })() && (
                                        <TabsTrigger value="expansion">{t('provisioningTemplates.tabs.expansion', 'KEY EXPANSION MODULES')}</TabsTrigger>
                                    )}
                                    {selectedModel.phonebook && (
                                        <TabsTrigger value="phonebooks">{t('provisioningTemplates.tabs.phonebooks', 'PHONEBOOKS')}</TabsTrigger>
                                    )}
                                    <TabsTrigger value="gui">{t('provisioningTemplates.tabs.gui', 'GUI SETTINGS')}</TabsTrigger>
                                    <TabsTrigger value="custom">{t('provisioningTemplates.tabs.custom', 'CUSTOM')}</TabsTrigger>
                                </>
                            )}
                        </TabsList>

                        <TabsContent value="general">
                            <GeneralTab control={control} register={register} errors={errors}
                                vendors={vendors} serverSettings={serverSettings} models={models}
                                selectedModel={selectedModel} watchedVendorId={watchedVendorId}
                                previewImageSrc={previewImageSrc} handleImageError={handleImageError} />
                        </TabsContent>

                        {selectedModel && (
                            <>
                                <TabsContent value="dss">
                                    <DssKeysTab selectedModel={selectedModel} dssKeys={dssKeys} setDssKeys={setDssKeysTyped}
                                        dssPage={dssPage} setDssPage={setDssPage} dssPerPage={dssPerPage} setDssPerPage={setDssPerPage}
                                        setValue={(f, v) => setValue(f, v)} />
                                </TabsContent>
                                <TabsContent value="phonebooks">
                                    <PhonebookTab selectedModel={selectedModel} phonebookEntries={phonebookEntries}
                                        setPhonebookEntries={setPhonebookEntriesTyped} availablePhonebooks={availablePhonebooks}
                                        setValue={(f, v) => setValue(f, v)} />
                                </TabsContent>
                                <TabsContent value="expansion">
                                    <ExpansionModulesTab selectedModel={selectedModel} expansionKeys={expansionKeys}
                                        setExpansionKeys={setExpansionKeysTyped} selectedExpModule={selectedExpModule}
                                        setSelectedExpModule={setSelectedExpModule} expPage={expPage} setExpPage={setExpPage}
                                        expPerPage={expPerPage} setExpPerPage={setExpPerPage} setValue={(f, v) => setValue(f, v)} />
                                </TabsContent>
                                <TabsContent value="gui" className="space-y-4">
                                    <GuiTab selectedModel={selectedModel} control={control} errors={errors}
                                        previewUrl={previewUrl} selectedWallpaperFile={selectedWallpaperFile}
                                        setSelectedWallpaperFile={setSelectedWallpaperFile} />
                                </TabsContent>
                                <TabsContent value="custom">
                                    <CustomTab selectedModel={selectedModel} control={control}
                                        isLoadingTemplate={isLoadingTemplate} originalTemplateContent={originalTemplateContent}
                                        getValues={(f) => getValues(f)} setValue={(f, v) => setValue(f, v)} />
                                </TabsContent>
                            </>
                        )}
                    </Tabs>
                </form>
            </div>

            <FixedActionBar onSaveWithAction={handleSaveWithAction} isSaving={isSubmitting} onCancel={handleCancel}
                cancelText={t('common.cancel', 'Cancel')} useSaveDropdown={true}
                saveDropdownStorageKey="provisioningTemplates:saveAction" saveDisabled={isSubmitting}
                isEditMode={!isNew} onDelete={!isNew ? () => setShowDeleteModal(true) : undefined} />

            <ConfirmationModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)}
                onConfirm={async () => {
                    setIsDeleting(true);
                    try {
                        await api.deleteTemplate(id!);
                        showSuccess(t('common.deletedSuccessfully', 'Item deleted successfully'));
                        navigate('/settings/provisioning/provisioning-templates');
                    } catch (error) {
                        console.error(error);
                        showError(t('common.deleteError', 'Failed to delete item'));
                    } finally { setIsDeleting(false); setShowDeleteModal(false); }
                }}
                title={t('common.deleteItem', 'Delete Template')} message={t('common.confirmDelete', 'Are you sure you want to delete this item?')}
                confirmText={t('common.delete', 'Delete')} cancelText={t('common.cancel', 'Cancel')} type="danger" isLoading={isDeleting} />

            <QuickSearchModal isOpen={showQuickList} onClose={() => { setShowQuickList(false); setQuickListSearch(''); }}
                title={t('common.quickSearch', 'Quick Search')} items={quickListItems}
                isLoadingInitial={isLoadingQuickListInitial} isSearching={isSearchingQuickList}
                searchValue={quickListSearch} onSearchChange={setQuickListSearch}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onItemSelect={(item: any) => { setShowQuickList(false); setQuickListSearch(''); navigate(`/settings/provisioning/provisioning-templates/${item.id}`); }}
                currentItemId={id}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                renderItem={(item: any, isCurrent: boolean) => (
                    <button onClick={() => { if (!isCurrent) { setShowQuickList(false); setQuickListSearch(''); navigate(`/settings/provisioning/provisioning-templates/${item.id}`); } }}
                        disabled={isCurrent}
                        className={`w-full text-left p-3 rounded-md transition-colors ${isCurrent ? 'bg-primary/20 border-2 border-primary cursor-default' : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'}`}>
                        <div className="flex items-center gap-3">
                            <LayoutTemplate className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold text-foreground">{item.name}</span>
                                    {isCurrent && <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">{t('common.current', 'Current')}</span>}
                                </div>
                                <div className="text-sm text-muted-foreground truncate">{item.model?.model} - {item.serverSettings?.name}</div>
                            </div>
                        </div>
                    </button>
                )}
                searchPlaceholder={t('common.searchPlaceholder', 'Search templates...')}
                minCharsMessage={t('common.minChars', 'Type 2 chars to search')}
                emptyMessage={t('common.startTyping', 'Start typing to search templates')}
                noResultsMessage={t('common.noResults', 'No templates found')}
                headerIcon={<LayoutTemplate className="h-5 w-5" />}
                minSearchChars={2} />
        </div>
    );
}
